import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Node,
  Edge,
  Connection,
  NodeChange,
  EdgeChange,
  useReactFlow,
  XYPosition,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { TriggerNode } from './nodes/TriggerNode';
import { ActionNode } from './nodes/ActionNode';
import { MenuNode } from './nodes/MenuNode';
import { EndNode } from './nodes/EndNode';
import { WaitForReplyNode } from './nodes/WaitForReplyNode';
import { ResolveTicketNode } from './nodes/ResolveTicketNode';
import { AskQuestionNode } from './nodes/AskQuestionNode';
import { Palette } from './Palette';
import { PropertiesPanel } from './PropertiesPanel';
import {
  FlowNodeType,
  FlowNodeTypeValue,
  FlowDefinitionDto,
  FlowNodeDto,
  FlowEdgeDto,
  EditorLookups,
  DotNetRef,
} from './types';

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  menu: MenuNode,
  end: EndNode,
  waitForReply: WaitForReplyNode,
  resolveTicket: ResolveTicketNode,
  askQuestion: AskQuestionNode,
};

// Mapeia FlowNodeType (numeric enum) → string nodeType usado no ReactFlow.
function rfTypeFor(type: FlowNodeTypeValue): keyof typeof nodeTypes {
  if (type === FlowNodeType.TriggerNewConversation || type === FlowNodeType.TriggerKeyword) return 'trigger';
  if (type === FlowNodeType.Menu) return 'menu';
  if (type === FlowNodeType.AskQuestion) return 'askQuestion';
  if (type === FlowNodeType.WaitForReply) return 'waitForReply';
  if (type === FlowNodeType.ResolveTicket) return 'resolveTicket';
  if (type === FlowNodeType.End) return 'end';
  return 'action';
}

export interface FlowEditorHandle {
  loadDefinition(def: FlowDefinitionDto): void;
  extractDefinition(): { nodes: FlowNodeDto[]; edges: FlowEdgeDto[] };
}

interface Props {
  initialDefinition: FlowDefinitionDto | null;
  lookups: EditorLookups;
  dotNetRef: DotNetRef;
  handleRef: React.MutableRefObject<FlowEditorHandle | null>;
}

export function FlowEditor(props: Props) {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} />
    </ReactFlowProvider>
  );
}

function FlowEditorInner({ initialDefinition, lookups, dotNetRef, handleRef }: Props) {
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  // Load initial definition once
  useEffect(() => {
    if (initialDefinition) {
      loadDefinitionInto(initialDefinition, setNodes, setEdges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Imperative handle for Blazor side
  useEffect(() => {
    handleRef.current = {
      loadDefinition: (def) => loadDefinitionInto(def, setNodes, setEdges),
      extractDefinition: () => extractDefinitionFrom(nodes, edges),
    };
  }, [nodes, edges, handleRef]);

  const notifyChanged = useCallback(() => {
    dotNetRef.invokeMethodAsync('OnChangedCallback').catch(() => { /* ignore */ });
  }, [dotNetRef]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    notifyChanged();
  }, [notifyChanged]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    notifyChanged();
  }, [notifyChanged]);

  const onConnect = useCallback((conn: Connection) => {
    setEdges((eds) => addEdge({ ...conn, id: crypto.randomUUID() }, eds));
    notifyChanged();
  }, [notifyChanged]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    dotNetRef.invokeMethodAsync('OnSelectionChangedCallback', node.id).catch(() => { /* ignore */ });
  }, [dotNetRef]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    dotNetRef.invokeMethodAsync('OnSelectionChangedCallback', null).catch(() => { /* ignore */ });
  }, [dotNetRef]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/wisertalk-node');
    if (!raw) return;
    const type = parseInt(raw, 10) as FlowNodeTypeValue;
    const position: XYPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const newNode: Node = {
      id: crypto.randomUUID(),
      type: rfTypeFor(type),
      position,
      data: { type, config: defaultConfigFor(type) },
    };
    setNodes((nds) => [...nds, newNode]);
    notifyChanged();
  }, [screenToFlowPosition, notifyChanged]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onConfigChange = useCallback((nodeId: string, newConfig: string) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, config: newConfig } } : n)),
    );
    setSelectedNode((sel) =>
      sel && sel.id === nodeId ? { ...sel, data: { ...sel.data, config: newConfig } } : sel,
    );
    notifyChanged();
  }, [notifyChanged]);

  const onDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    dotNetRef.invokeMethodAsync('OnSelectionChangedCallback', null).catch(() => { /* ignore */ });
    notifyChanged();
  }, [dotNetRef, notifyChanged]);

  const nodeTypesMemo = useMemo(() => nodeTypes, []);

  return (
    <div className="wt-rf-shell">
      <Palette />
      <div className="wt-rf-canvas" onDrop={onDrop} onDragOver={onDragOver}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypesMemo}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
      <PropertiesPanel
        selected={selectedNode}
        lookups={lookups}
        allNodes={nodes}
        allEdges={edges}
        onConfigChange={onConfigChange}
        onDelete={onDeleteNode}
      />
    </div>
  );
}

function defaultConfigFor(type: FlowNodeTypeValue): string {
  switch (type) {
    case FlowNodeType.TriggerKeyword:
      return JSON.stringify({ keywords: [] });
    case FlowNodeType.Menu:
      return JSON.stringify({ message: '', options: [] });
    case FlowNodeType.AiAgent:
      return JSON.stringify({ systemPrompt: '', maxTurns: 10 });
    case FlowNodeType.Wait:
      return JSON.stringify({ durationSeconds: 5, waitType: 'time' });
    case FlowNodeType.WaitForReply:
      return JSON.stringify({ timeoutMinutes: 10 });
    case FlowNodeType.ResolveTicket:
      return JSON.stringify({ categoryId: '', message: '' });
    default:
      return '{}';
  }
}

function loadDefinitionInto(
  def: FlowDefinitionDto,
  setNodes: (n: Node[]) => void,
  setEdges: (e: Edge[]) => void,
) {
  const nodes: Node[] = def.nodes.map((dto) => ({
    id: dto.id,
    type: rfTypeFor(dto.type),
    position: { x: dto.positionX, y: dto.positionY },
    data: { type: dto.type, config: dto.config || '{}' },
  }));
  const edges: Edge[] = def.edges.map((dto) => ({
    id: dto.id,
    source: dto.sourceNodeId,
    target: dto.targetNodeId,
    sourceHandle: extractMenuSourceHandle(dto.condition),
    data: { condition: dto.condition },
  }));
  setNodes(nodes);
  setEdges(edges);
}

function extractMenuSourceHandle(condition: string | null): string | undefined {
  if (!condition) return undefined;
  try {
    const c = JSON.parse(condition) as { value?: string };
    return c.value;
  } catch {
    return undefined;
  }
}

function extractDefinitionFrom(nodes: Node[], edges: Edge[]): { nodes: FlowNodeDto[]; edges: FlowEdgeDto[] } {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      type: ((n.data as { type?: FlowNodeTypeValue }).type) ?? FlowNodeType.SendMessage,
      positionX: n.position.x,
      positionY: n.position.y,
      config: (n.data as { config?: string }).config ?? '{}',
    })),
    edges: edges.map((e) => ({
      id: e.id,
      sourceNodeId: e.source,
      targetNodeId: e.target,
      condition: e.sourceHandle ? JSON.stringify({ value: e.sourceHandle }) : null,
    })),
  };
}
